import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail, Phone, Calendar, DollarSign, Clock, CheckCircle, XCircle, AlertCircle, Edit, UserCheck, Activity, Save, Plus, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

import staffService from '../../../services/staffService';

const StaffDetailsModal = ({ isOpen, staffMember, staff, events = [], onClose, onUpdate, onAssignmentUpdate, userProfile }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Add missing state for success messages
  const [successMessage, setSuccessMessage] = useState(null);
  const modalRef = useRef(null);
  
  // Edit form states
  const [editedStaff, setEditedStaff] = useState({});
  const [editedAssignments, setEditedAssignments] = useState([]);
  const [newAssignment, setNewAssignment] = useState({
    eventId: '',
    roleDescription: '',
    shiftStart: '',
    shiftEnd: '',
    notes: ''
  });
  const [showNewAssignmentForm, setShowNewAssignmentForm] = useState(false);

  // Initialize form data when staff changes
  useEffect(() => {
    if (staffMember) {
      setEditedStaff({
        staff_role: staffMember?.staff_role || '',
        phone_number: staffMember?.phone_number || '',
        emergency_contact: staffMember?.emergency_contact || '',
        hourly_rate: staffMember?.hourly_rate || '',
        notes: staffMember?.notes || '',
        skills: staffMember?.skills || [],
        status: staffMember?.status || 'active'
      });
      setEditedAssignments(staffMember?.staff_event_assignments || []);
    }
  }, [staffMember]);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event?.key === 'Escape' && isOpen) {
        if (isEditMode) {
          handleCancelEdit();
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isEditMode]);

  // Handle outside click
  const handleBackdropClick = (event) => {
    if (modalRef?.current && !modalRef?.current?.contains(event?.target)) {
      if (isEditMode) {
        handleCancelEdit();
      } else {
        onClose();
      }
    }
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      handleCancelEdit();
    } else {
      setIsEditMode(true);
      setError(null);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setShowNewAssignmentForm(false);
    setError(null);
    // Reset form data
    if (staffMember) {
      setEditedStaff({
        staff_role: staffMember?.staff_role || '',
        phone_number: staffMember?.phone_number || '',
        emergency_contact: staffMember?.emergency_contact || '',
        hourly_rate: staffMember?.hourly_rate || '',
        notes: staffMember?.notes || '',
        skills: staffMember?.skills || [],
        status: staffMember?.status || 'active'
      });
      setEditedAssignments(staffMember?.staff_event_assignments || []);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Update staff details first
      const result = await onUpdate?.(staffMember?.id, editedStaff);
      if (result?.error) {
        throw new Error(result?.error);
      }

      // Update assignments if there are changes
      const originalAssignments = staffMember?.staff_event_assignments || [];
      const updatedAssignments = editedAssignments;

      // Find assignments that were modified
      for (const updatedAssignment of updatedAssignments) {
        const originalAssignment = originalAssignments?.find(orig => orig?.id === updatedAssignment?.id);
        
        if (originalAssignment && hasAssignmentChanges(originalAssignment, updatedAssignment)) {
          const assignmentUpdates = {
            role_description: updatedAssignment?.role_description,
            shift_start: updatedAssignment?.shift_start,
            shift_end: updatedAssignment?.shift_end,
            notes: updatedAssignment?.notes
          };

          const assignmentResult = await staffService?.updateStaffAssignment(
            updatedAssignment?.id, 
            assignmentUpdates, 
            userProfile
          );
                  
          if (assignmentResult?.error) {
            console.error('Error updating assignment:', assignmentResult?.error);
          }
        }
      }
      
      setIsEditMode(false);
      setSuccessMessage('Staff details updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (err) {
      console.error('Error saving changes:', err);
      setError(err?.message || 'Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to check if assignment has changes
  const hasAssignmentChanges = (original, updated) => {
    return (
      original?.role_description !== updated?.role_description ||
      original?.shift_start !== updated?.shift_start ||
      original?.shift_end !== updated?.shift_end ||
      original?.notes !== updated?.notes
    );
  };

  // Handle assignment check-in with proper service integration
  const handleCheckIn = async (assignmentId) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await staffService?.checkInStaff(assignmentId, userProfile);
      if (result?.error) {
        setError(`Check-in failed: ${result?.error}`);
        return;
      }
      
      // Update local assignment state
      setEditedAssignments(prev =>
        prev?.map(assignment =>
          assignment?.id === assignmentId
            ? { ...assignment, check_in_time: new Date()?.toISOString(), status: 'checked_in' }
            : assignment
        )
      );

      setSuccessMessage('Staff checked in successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Update staff last activity
      onUpdate?.(staffMember?.id, { last_activity: new Date()?.toISOString() });

    } catch (err) {
      console.error('Error during check-in:', err);
      setError('Failed to check in staff member');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle assignment check-out with proper service integration
  const handleCheckOut = async (assignmentId) => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await staffService?.checkOutStaff(assignmentId, userProfile);
      if (result?.error) {
        setError(`Check-out failed: ${result?.error}`);
        return;
      }
      
      // Update local assignment state
      setEditedAssignments(prev =>
        prev?.map(assignment =>
          assignment?.id === assignmentId
            ? { ...assignment, check_out_time: new Date()?.toISOString(), status: 'completed' }
            : assignment
        )
      );

      setSuccessMessage('Staff checked out successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      
      // Update staff last activity
      onUpdate?.(staffMember?.id, { last_activity: new Date()?.toISOString() });

    } catch (err) {
      console.error('Error during check-out:', err);
      setError('Failed to check out staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStaffFieldChange = (field, value) => {
    setEditedStaff(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSkillAdd = (skill) => {
    if (skill && !editedStaff?.skills?.includes(skill)) {
      setEditedStaff(prev => ({
        ...prev,
        skills: [...(prev?.skills || []), skill]
      }));
    }
  };

  const handleSkillRemove = (skillToRemove) => {
    setEditedStaff(prev => ({
      ...prev,
      skills: prev?.skills?.filter(skill => skill !== skillToRemove) || []
    }));
  };

  const handleAssignmentFieldChange = (assignmentId, field, value) => {
    setEditedAssignments(prev =>
      prev?.map(assignment =>
        assignment?.id === assignmentId
          ? { ...assignment, [field]: value }
          : assignment
      )
    );
  };

  const handleAddNewAssignment = () => {
    setShowNewAssignmentForm(true);
  };

  const handleNewAssignmentSubmit = async () => {
    try {
      if (!newAssignment?.eventId) {
        setError('Please select an event for the assignment');
        return;
      }

      setIsLoading(true);
      setError(null);
      
      // Create assignment data using the existing staffService
      const assignmentData = {
        staffId: staffMember?.id,
        eventId: newAssignment?.eventId,
        roleDescription: newAssignment?.roleDescription,
        shiftStart: newAssignment?.shiftStart,
        shiftEnd: newAssignment?.shiftEnd,
        notes: newAssignment?.notes
      };

      // Call the existing createStaffAssignment service method
      const result = await staffService?.createStaffAssignment(assignmentData, userProfile);
      if (result?.error) {
        throw new Error(result?.error);
      }
      
      // Update local state with the new assignment
      const newAssignmentWithData = {
        ...result?.data,
        events: events?.find(e => e?.id === assignmentData?.eventId)
      };
      
      setEditedAssignments(prev => [...prev, newAssignmentWithData]);
      
      setShowNewAssignmentForm(false);
      setNewAssignment({
        eventId: '',
        roleDescription: '',
        shiftStart: '',
        shiftEnd: '',
        notes: ''
      });

      // Call parent update to refresh data
      if (onAssignmentUpdate) {
        await onAssignmentUpdate(result?.data?.id, result?.data);
      }

    } catch (err) {
      console.error('Error adding new assignment:', err);
      setError(err?.message || 'Failed to add assignment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Call the existing removeStaffAssignment service method
      const result = await staffService?.removeStaffAssignment(assignmentId, userProfile);
      if (result?.error) {
        throw new Error(result?.error);
      }
      
      // Update local state by removing the assignment
      setEditedAssignments(prev =>
        prev?.filter(assignment => assignment?.id !== assignmentId)
      );

      // Call parent update to refresh data
      if (onAssignmentUpdate) {
        await onAssignmentUpdate(assignmentId, null);
      }

    } catch (err) {
      console.error('Error removing assignment:', err);
      setError(err?.message || 'Failed to remove assignment');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !staffMember) return null;

  const staffUserProfile = staffMember?.user_profiles;
  const assignments = isEditMode ? editedAssignments : (staffMember?.staff_event_assignments || []);
  
  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      inactive: { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle },
      suspended: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle }
    };

    const badge = badges?.[status] || badges?.inactive;
    const IconComponent = badge?.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge?.color}`}>
        <IconComponent className="w-4 h-4 mr-1" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  // Get assignment status badge
  const getAssignmentStatusBadge = (assignment) => {
    const isUpcoming = new Date(assignment?.events?.event_date) > new Date();
    const status = assignment?.status;
    
    let color = 'bg-gray-100 text-gray-800';
    let icon = Clock;
    
    if (status === 'completed') {
      color = 'bg-green-100 text-green-800';
      icon = CheckCircle;
    } else if (status === 'assigned' && isUpcoming) {
      color = 'bg-blue-100 text-blue-800';
      icon = Clock;
    } else if (status === 'cancelled') {
      color = 'bg-red-100 text-red-800';
      icon = XCircle;
    }

    const IconComponent = icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const staffRoleOptions = [
    { value: 'check_in_staff', label: 'Check-in Staff' },
    { value: 'security_staff', label: 'Security Staff' },
    { value: 'technical_staff', label: 'Technical Staff' },
    { value: 'customer_service_staff', label: 'Customer Service Staff' },
    { value: 'coordinator_staff', label: 'Coordinator Staff' },
    { value: 'supervisor_staff', label: 'Supervisor Staff' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const availableSkills = [
    'registration', 'check_in', 'customer_service', 'technical_support', 
    'security', 'event_coordination', 'logistics', 'hospitality'
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'assignments', label: 'Assignments', icon: Calendar },
    { id: 'activity', label: 'Activity', icon: Activity }
  ];

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={handleBackdropClick}
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e?.stopPropagation()} // Prevent click from bubbling to backdrop
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-blue-600 font-medium text-lg">
                  {staffUserProfile?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {staffUserProfile?.full_name || 'Unknown Staff'}
                </h2>
                <p className="text-gray-600">
                  {staffMember?.staff_role?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'Staff Member'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {getStatusBadge(isEditMode ? editedStaff?.status : staffMember?.status)}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="px-6 py-4 bg-green-50 border-b border-green-200">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <p className="text-green-700 text-sm">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="px-6 py-4 bg-red-50 border-b border-red-200">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs?.map((tab) => {
                const IconComponent = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab?.id
                        ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <IconComponent className="w-4 h-4 mr-2" />
                    {tab?.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-350px)]">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <Mail className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Email</p>
                          <p className="text-sm text-gray-600">{staffUserProfile?.email || 'Not provided'}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Phone className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Phone</p>
                          {isEditMode ? (
                            <Input
                              type="tel"
                              value={editedStaff?.phone_number || ''}
                              onChange={(e) => handleStaffFieldChange('phone_number', e?.target?.value)}
                              placeholder="Phone number"
                              className="mt-1"
                            />
                          ) : (
                            <p className="text-sm text-gray-600">
                              {staffMember?.phone_number || staffUserProfile?.phone || 'Not provided'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <Calendar className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Hire Date</p>
                          <p className="text-sm text-gray-600">
                            {staffMember?.hire_date ? format(new Date(staffMember?.hire_date), 'MMMM d, yyyy') : 'Not provided'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <DollarSign className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Hourly Rate</p>
                          {isEditMode ? (
                            <Input
                              type="number"
                              value={editedStaff?.hourly_rate || ''}
                              onChange={(e) => handleStaffFieldChange('hourly_rate', e?.target?.value)}
                              placeholder="Hourly rate"
                              className="mt-1"
                            />
                          ) : (
                            <p className="text-sm text-gray-600">
                              {staffMember?.hourly_rate ? `$${staffMember?.hourly_rate}/hour` : 'Not set'}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Staff Role and Status */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Role & Status</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Staff Role</label>
                      {isEditMode ? (
                        <Select
                          value={editedStaff?.staff_role || ''}
                          onChange={(value) => handleStaffFieldChange('staff_role', value)}
                          options={staffRoleOptions}
                          placeholder="Select role"
                        />
                      ) : (
                        <p className="text-sm text-gray-600 py-2">
                          {staffMember?.staff_role?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'Not set'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Status</label>
                      {isEditMode ? (
                        <Select
                          value={editedStaff?.status || ''}
                          onChange={(value) => handleStaffFieldChange('status', value)}
                          options={statusOptions}
                          placeholder="Select status"
                        />
                      ) : (
                        <div className="py-2">
                          {getStatusBadge(staffMember?.status)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h3>
                  {isEditMode ? (
                    <Input
                      type="tel"
                      value={editedStaff?.emergency_contact || ''}
                      onChange={(e) => handleStaffFieldChange('emergency_contact', e?.target?.value)}
                      placeholder="Emergency contact number"
                    />
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-900">
                        {staffMember?.emergency_contact || 'Not provided'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Skills */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Skills & Qualifications</h3>
                  {isEditMode ? (
                    <div className="space-y-4">
                      <div className="flex flex-wrap gap-2">
                        {editedStaff?.skills?.map((skill) => (
                          <span
                            key={skill}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200"
                            onClick={() => handleSkillRemove(skill)}
                          >
                            {skill?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                            <X className="w-3 h-3 ml-1" />
                          </span>
                        ))}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Add Skills</label>
                        <div className="flex flex-wrap gap-2">
                          {availableSkills?.filter(skill => !editedStaff?.skills?.includes(skill))?.map((skill) => (
                            <button
                              key={skill}
                              onClick={() => handleSkillAdd(skill)}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              {skill?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {staffMember?.skills?.map((skill) => (
                        <span
                          key={skill}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                        >
                          {skill?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                        </span>
                      )) || <p className="text-gray-500">No skills listed</p>}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Notes</h3>
                  {isEditMode ? (
                    <textarea
                      value={editedStaff?.notes || ''}
                      onChange={(e) => handleStaffFieldChange('notes', e?.target?.value)}
                      placeholder="Add notes about this staff member..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={4}
                    />
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-900 whitespace-pre-line">
                        {staffMember?.notes || 'No notes added'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'assignments' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Event Assignments</h3>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">
                      {assignments?.length || 0} total assignments
                    </span>
                    {isEditMode && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddNewAssignment}
                        disabled={showNewAssignmentForm}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Assignment
                      </Button>
                    )}
                  </div>
                </div>

                {/* New Assignment Form */}
                {showNewAssignmentForm && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                    <h4 className="text-md font-medium text-blue-900">New Assignment</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
                        <Select
                          value={newAssignment?.eventId}
                          onChange={(value) => setNewAssignment(prev => ({ ...prev, eventId: value }))}
                          options={events?.map(event => ({
                            value: event?.id,
                            label: `${event?.title} - ${event?.event_date ? format(new Date(event?.event_date), 'MMM d, yyyy') : 'No date'}`
                          }))}
                          placeholder="Select an event"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role Description</label>
                        <Input
                          value={newAssignment?.roleDescription}
                          onChange={(e) => setNewAssignment(prev => ({ ...prev, roleDescription: e?.target?.value }))}
                          placeholder="e.g., Main entrance check-in"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Shift Start</label>
                        <Input
                          type="datetime-local"
                          value={newAssignment?.shiftStart}
                          onChange={(e) => setNewAssignment(prev => ({ ...prev, shiftStart: e?.target?.value }))}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Shift End</label>
                        <Input
                          type="datetime-local"
                          value={newAssignment?.shiftEnd}
                          onChange={(e) => setNewAssignment(prev => ({ ...prev, shiftEnd: e?.target?.value }))}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                        <Input
                          value={newAssignment?.notes}
                          onChange={(e) => setNewAssignment(prev => ({ ...prev, notes: e?.target?.value }))}
                          placeholder="Additional assignment notes..."
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowNewAssignmentForm(false)}
                        disabled={isLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleNewAssignmentSubmit}
                        disabled={isLoading || !newAssignment?.eventId}
                      >
                        Add Assignment
                      </Button>
                    </div>
                  </div>
                )}

                {assignments?.length > 0 ? (
                  <div className="space-y-4">
                    {assignments?.map((assignment) => (
                      <div 
                        key={assignment?.id}
                        className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h4 className="text-lg font-medium text-gray-900 mb-1">
                              {assignment?.events?.title || 'Unknown Event'}
                            </h4>
                            {isEditMode ? (
                              <Input
                                value={assignment?.role_description || ''}
                                onChange={(e) => handleAssignmentFieldChange(assignment?.id, 'role_description', e?.target?.value)}
                                placeholder="Role description"
                                className="mt-2"
                              />
                            ) : (
                              <p className="text-sm text-gray-600">
                                {assignment?.role_description || 'No role description'}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {getAssignmentStatusBadge(assignment)}
                            {isEditMode && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveAssignment(assignment?.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                disabled={isLoading}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div className="space-y-2">
                            <div className="flex items-center text-gray-600">
                              <Calendar className="w-4 h-4 mr-2" />
                              {assignment?.events?.event_date && 
                                format(new Date(assignment?.events?.event_date), 'EEEE, MMMM d, yyyy')
                              }
                            </div>
                            
                            <div className="space-y-1">
                              <label className="block text-xs font-medium text-gray-700">Shift Times</label>
                              {isEditMode ? (
                                <div className="flex space-x-2">
                                  <Input
                                    type="datetime-local"
                                    value={assignment?.shift_start ? 
                                      format(new Date(assignment?.shift_start), "yyyy-MM-dd'T'HH:mm") : ''
                                    }
                                    onChange={(e) => handleAssignmentFieldChange(assignment?.id, 'shift_start', e?.target?.value)}
                                    className="text-xs"
                                  />
                                  <Input
                                    type="datetime-local"
                                    value={assignment?.shift_end ? 
                                      format(new Date(assignment?.shift_end), "yyyy-MM-dd'T'HH:mm") : ''
                                    }
                                    onChange={(e) => handleAssignmentFieldChange(assignment?.id, 'shift_end', e?.target?.value)}
                                    className="text-xs"
                                  />
                                </div>
                              ) : (
                                assignment?.shift_start && assignment?.shift_end && (
                                  <div className="flex items-center text-gray-600">
                                    <Clock className="w-4 h-4 mr-2" />
                                    {format(new Date(assignment?.shift_start), 'HH:mm')} - 
                                    {format(new Date(assignment?.shift_end), 'HH:mm')}
                                  </div>
                                )
                              )}
                            </div>
                          </div>

                          <div className="space-y-2">
                            {assignment?.assignment_date && (
                              <div className="flex items-center text-gray-600">
                                <UserCheck className="w-4 h-4 mr-2" />
                                Assigned {format(new Date(assignment?.assignment_date), 'MMM d, yyyy')}
                              </div>
                            )}

                            {(assignment?.check_in_time || assignment?.check_out_time) && (
                              <div className="text-gray-600">
                                <div className="flex items-center">
                                  <Activity className="w-4 h-4 mr-2" />
                                  Check-in Activity
                                </div>
                                {assignment?.check_in_time && (
                                  <div className="ml-6 text-xs">
                                    In: {format(new Date(assignment?.check_in_time), 'MMM d, HH:mm')}
                                  </div>
                                )}
                                {assignment?.check_out_time && (
                                  <div className="ml-6 text-xs">
                                    Out: {format(new Date(assignment?.check_out_time), 'MMM d, HH:mm')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {(assignment?.notes || isEditMode) && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                            {isEditMode ? (
                              <textarea
                                value={assignment?.notes || ''}
                                onChange={(e) => handleAssignmentFieldChange(assignment?.id, 'notes', e?.target?.value)}
                                placeholder="Assignment notes..."
                                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                rows={2}
                              />
                            ) : (
                              <p className="text-sm text-gray-600">
                                {assignment?.notes || 'No notes'}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Assignment Actions */}
                        {assignment?.status === 'assigned' && new Date() >= new Date(assignment?.shift_start) && (
                          <div className="flex items-center space-x-2">
                            {!assignment?.check_in_time ? (
                              <Button
                                size="sm"
                                onClick={() => handleCheckIn(assignment?.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Check In
                              </Button>
                            ) : !assignment?.check_out_time ? (
                              <Button
                                size="sm"
                                onClick={() => handleCheckOut(assignment?.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                Check Out
                              </Button>
                            ) : (
                              <span className="text-sm text-green-600 font-medium">
                                ✓ Completed
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Assignments Yet</h3>
                    <p className="text-gray-500">This staff member has not been assigned to any events.</p>
                    {isEditMode && (
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={handleAddNewAssignment}
                        disabled={showNewAssignmentForm}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Assignment
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
                
                <div className="space-y-4">
                  {/* Last Activity */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Last Activity</p>
                          <p className="text-xs text-gray-600">
                            {staffMember?.last_activity ? 
                              format(new Date(staffMember?.last_activity), 'MMMM d, yyyy \'at\' HH:mm') : 
                              'No recent activity'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Account Created */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <UserCheck className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Staff Record Created</p>
                          <p className="text-xs text-gray-600">
                            {staffMember?.created_at && format(new Date(staffMember?.created_at), 'MMMM d, yyyy \'at\' HH:mm')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Assignments Activity */}
                  {assignments?.slice(0, 3)?.map((assignment) => (
                    <div key={assignment?.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Calendar className="w-5 h-5 text-blue-500 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              Assigned to {assignment?.events?.title}
                            </p>
                            <p className="text-xs text-gray-600">
                              {assignment?.assignment_date && 
                                format(new Date(assignment?.assignment_date), 'MMMM d, yyyy \'at\' HH:mm')
                              }
                            </p>
                          </div>
                        </div>
                        {getAssignmentStatusBadge(assignment)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isLoading}
              >
                {isEditMode ? 'Cancel' : 'Close'}
              </button>
            </div>
            
            <div className="flex items-center space-x-4">
              {isEditMode ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={isLoading}
                  >
                    Cancel Changes
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleEditToggle}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Staff
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default StaffDetailsModal;